import { useMemo, Fragment } from "react"
import { numberFormat, round } from "shared/util"
import { Button, ButtonProps, Stack, Typography, styled, Box, Divider } from "@mui/material"
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip'
import { TrackChanges as PredictionIcon } from '@mui/icons-material'

import { ErrorBlock } from "components/controls/errorInfo"

import { KojinResult } from "interface/kojin"
import { usePrediction } from "../hooks"

type ErrorTooltipProps = {
	errors: Error[]
} & TooltipProps
const ErrorTooltip = styled(({ className, errors, ...props }: ErrorTooltipProps) => (
  <Tooltip
		{...props}
		title={
			<Fragment>
				<ErrorBlock error={errors}/>
			</Fragment>
		}
		classes={{ popper: className }}
	/>
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    padding: 0,
    backgroundColor: 'rgba(0,0,0,0)',
		boxShadow: theme.shadows[1]
	}
}))

type PredictionBtnTooltipProps = {
	state: ReturnType<typeof usePrediction>
} & TooltipProps
const PredictionBtnTooltip = ({
	state,
	title,
	children
}: PredictionBtnTooltipProps) => {
	const {
		posting,
		errors,
		predictionResult: result
	} = state

	if (posting) {
		return (
			<Tooltip arrow title='予測中..'>
				{children}
			</Tooltip>
		)
	}
	if (errors.length > 0) {
		return (
			<ErrorTooltip errors={errors} title='error'>
				{children}
			</ErrorTooltip>
		)
	}
	if (!result) {
		return (
			<Tooltip arrow title={title}>
				{children}
			</Tooltip>
		)
	}
	return (
		<Tooltip
			arrow
			title={
				<Fragment>
					<Stack direction='column'>
						<Stack direction='row' spacing={0.5} alignItems='baseline'>
							<Typography variant="body2">対象</Typography>
							<Typography variant="body1">{numberFormat(result.uploadResult.postData.recordCount)}</Typography>
							<Typography variant="body2">人</Typography>
							<Typography variant="body2">×</Typography>
							<Typography variant="body1">{round(result.predictionResult.predictedViewRate, 2)}</Typography>
							<Typography variant="body2">%</Typography>
							<Typography variant="body2">=</Typography>
							<Typography variant="body1">{numberFormat(round((result.uploadResult.postData.recordCount * result.predictionResult.predictedViewRate) / 100))}</Typography>
							<Typography variant="body2">人</Typography>
						</Stack>
						<Divider/>
						<Box>
							<Typography variant="body2">{result.uploadResult.sharedList.condition}</Typography>
							<Typography variant="body2">{
							 (result.predictionCondition.source === 'mrp' ? 'MRPlus' :
								result.predictionCondition.source === 'lv' ? 'Web講演会' : '') + ' : ' + result.predictionCondition.text}</Typography>
						</Box>
					</Stack>
				</Fragment>
			}
		>
			{children}
		</Tooltip>
	)
}

type PredictionBtnProps = {
	kojinList: KojinResult[]
	state: ReturnType<typeof usePrediction>
} & ButtonProps
export const PredictionBtn = ({
	kojinList,
	state,
	...others
}: PredictionBtnProps) => {
	const {
		posting,
		errors,
		predictionResult
	} = state

	const title = useMemo(
		() => {
			if (posting) return '予測中..'
			if (errors.length > 0) return 'エラー'
			if (predictionResult) return `${round(predictionResult.predictionResult.predictedViewRate, 2)} %`
			return '視聴予測'
		},
		[posting, errors, predictionResult]
	)
	return (
		<PredictionBtnTooltip title='視聴予測' state={state}>
			<Button
				{...others}
				variant="outlined"
				size="medium"
				sx={{ whiteSpace: 'nowrap' }}
				loading={posting}
				loadingPosition="start"
				startIcon={<PredictionIcon/>}
				disabled={kojinList.length === 0}
			>
				{title}
			</Button>
		</PredictionBtnTooltip>
	)
}